import React from 'react';
import { generatePuzzlePath } from '../components/jigsaw/puzzlePathGenerator';

/**
 * Генерирует данные о гранях между пазлами
 * @param {number} rows - Количество строк
 * @param {number} cols - Количество колонок
 * @param {number} baseSeed - Базовый сид
 * @returns {Object} Объект граней
 */
function generateEdges(rows, cols, baseSeed) {
  const edges = { horizontal: {}, vertical: {} };

  // Простая, но хаотичная функция псевдослучайности
  const random = (seed) => {
    const x = Math.sin(seed * 123.456) * 1000;
    return x - Math.floor(x); // от 0 до 1
  };

  // Горизонтальные грани
  for (let r = 0; r < rows - 1; r++) {
    for (let c = 0; c < cols; c++) {
      const edgeId = `h_${r}_${c}`;
      const seed = baseSeed + r * cols + c * 17; // Уникальный сдвиг
      const randValue = random(seed);
      const direction = randValue < 0.5 ? 'down' : 'up';
      edges.horizontal[edgeId] = { seed, direction };
    }
  }

  // Вертикальные грани
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols - 1; c++) {
      const edgeId = `v_${r}_${c}`;
      const seed = baseSeed + r * cols + c * 23; // Другой множитель для разнообразия
      const randValue = random(seed);
      const direction = randValue < 0.5 ? 'right' : 'left';
      edges.vertical[edgeId] = { seed, direction };
    }
  }

  return edges;
}

/**
 * Вычисляет оптимальную сетку для пазла, где кусочки максимально близки к квадрату
 * @param {Object} imageDimensions - Размеры изображения {width, height}
 * @param {number} desiredPieceCount - Желаемое количество кусочков
 * @returns {Object} Объект с количеством строк и столбцов {rows, cols}
 */
function calculateOptimalGrid(imageDimensions, desiredPieceCount) {
  let bestRatio = Infinity;
  let bestRows = 0;
  let bestCols = 0;

  // Перебираем возможные варианты сетки
  for (let testRows = 2; testRows <= Math.ceil(Math.sqrt(desiredPieceCount) * 2); testRows++) {
    const testCols = Math.ceil(desiredPieceCount / testRows);
    
    // Размеры кусочка при такой сетке
    const pieceWidth = imageDimensions.width / testCols;
    const pieceHeight = imageDimensions.height / testRows;
    
    // Насколько далеко от квадрата (1.0 - идеальный квадрат)
    const pieceRatio = Math.abs(pieceWidth / pieceHeight - 1);
    
    if (pieceRatio < bestRatio) {
      bestRatio = pieceRatio;
      bestRows = testRows;
      bestCols = testCols;
    }
  }

  return { rows: bestRows, cols: bestCols };
}

/**
 * Основная функция генерации данных для всех пазлов
 */
export const generatePieces = async (imageBuffer, config, ref) => {
  // Простая, но хаотичная функция псевдослучайности (локальная)
  const random = (seed) => {
    const x = Math.sin(seed * 123.456) * 1000;
    return x - Math.floor(x); // от 0 до 1
  };

  return new Promise((resolve, reject) => {
    const blob = new Blob([imageBuffer], { type: 'image/png' });
    const blobUrl = URL.createObjectURL(blob);
    const img = new Image();

    img.onload = async () => {
      try {
        let imageDimensions = { width: img.naturalWidth, height: img.naturalHeight };
        if (!imageDimensions.width || !imageDimensions.height) {
          throw new Error("Image loaded with zero dimensions.");
        }

        // --- ЛОГИКА ОБРЕЗКИ (CROP) ---
        let newBlobUrl = null;
        if (ref.info.game?.isRandom) {
            const aspect = imageDimensions.width / imageDimensions.height;
            let targetWidth = imageDimensions.width;
            let targetHeight = imageDimensions.height;
            let sx = 0;
            let sy = 0;
            let needsCrop = false;

            // Вертикальное: если слишком узкое/высокое (пропорция < 2:3)
            if (imageDimensions.width < imageDimensions.height) {
                const minAspect = 2 / 3;
                if (aspect < minAspect) {
                    targetHeight = Math.round(imageDimensions.width / minAspect);
                    sy = (imageDimensions.height - targetHeight) / 2;
                    needsCrop = true;
                }
            }
            // Горизонтальное: если слишком широкое (пропорция > 3:2)
            else {
                const maxAspect = 3 / 2;
                if (aspect > maxAspect) {
                    targetWidth = Math.round(imageDimensions.height * maxAspect);
                    sx = (imageDimensions.width - targetWidth) / 2;
                    needsCrop = true;
                }
            }

            if (needsCrop) {
                const canvas = document.createElement('canvas');
                canvas.width = targetWidth;
                canvas.height = targetHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, sx, sy, targetWidth, targetHeight, 0, 0, targetWidth, targetHeight);

                // Обновляем размеры для дальнейшего расчета сетки
                imageDimensions = { width: targetWidth, height: targetHeight };

                // Создаем новый BlobURL для обрезанного изображения
                await new Promise(r => canvas.toBlob(b => {
                    newBlobUrl = URL.createObjectURL(b);
                    r();
                }, 'image/png'));
            }
        }
        // -----------------------------

        const desiredPieceCount = config.game.puzzlePieceCount || 36;

        // Используем улучшенный алгоритм для вычисления оптимальной сетки
        const grid = calculateOptimalGrid(imageDimensions, desiredPieceCount);
        const rows = grid.rows;
        const cols = grid.cols;

        const pieceWidthBase = imageDimensions.width / cols;
        const pieceHeightBase = imageDimensions.height / rows;
        const pieceBaseDimensions = { width: pieceWidthBase, height: pieceHeightBase };

        const baseTabSizePercent = config.baseTabSizePercent;


        const maxSide = Math.max(pieceWidthBase, pieceHeightBase);
        const oneTabExtension = maxSide * baseTabSizePercent;
        const totalSide = maxSide + oneTabExtension * 2;
        const tabs = {
          width: (totalSide - pieceWidthBase) / 2,
          height: (totalSide - pieceHeightBase) / 2,
        }

        const pieces = {};
        const baseSeed = ref.info.game?.id || 12345;

        // Генерируем данные о гранях
        ref.puzzle = ref.puzzle || {};
        ref.puzzle.edges = generateEdges(rows, cols, baseSeed);

        // Расчет позиций в трее



        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            const pieceId = `piece_${r}_${c}`;

            // Конфигурация для генерации пути
            const pieceConfigForPath = {
              width: pieceWidthBase,
              height: pieceHeightBase,
              puzzleTabSize: config.game.puzzleTabSize,
              puzzleJitter: config.game.puzzleJitter,
              puzzleType: config.game.puzzleType,
              seed: baseSeed,
              position: { row: r, col: c },
              grid: { rows, cols },
              edges: ref.puzzle.edges,
            };
            const piecePath = generatePuzzlePath(pieceConfigForPath);


            const correctPos = { x: c / cols, y: r / rows };

            // ---- Изменено: Псевдослучайное вращение на основе сида и координат ----
            let initialRotation = 0;
            if (config.game.pieceRotation) {
              const rotations = [0, 90, 180, 270];
              const rotationSeed = baseSeed + r * cols + c * 31; // Другой множитель для уникальности
              const randomRotationValue = random(rotationSeed); 
              initialRotation = rotations[Math.floor(randomRotationValue * rotations.length)];
            }
            // ---- Конец изменения ----

            pieces[pieceId] = {
              id: pieceId,
              correctPos,

              placed: false,
              placedBy: null,
              inTray: true,
              onBoard: false,
              zIndex: 1,
              order: Math.random(),
              imageCrop: {
                x: c * pieceWidthBase,
                y: r * pieceHeightBase,
                width: pieceWidthBase,
                height: pieceHeightBase,
                tabs,
              },
              baseDimensions: { width: pieceWidthBase, height: pieceHeightBase },
              path: piecePath,
              pathConfig: { ...pieceConfigForPath }, // Сохраняем конфиг для повторного рендера
              reserved: 0,
              unreserved: 0,
              visible: true,
              rotation: initialRotation, 
            };
          }
        }

        resolve({
          pieces,
          imageDimensions,
          pieceBaseDimensions,
          rows,
          cols,
          newBlobUrl,
        });
      } catch (error) {
        console.error("Error during image processing or piece generation:", error);
        URL.revokeObjectURL(blobUrl);
        reject(error);
      }
    };

    img.onerror = () => {
      console.error("Failed to load image from blobUrl");
      URL.revokeObjectURL(blobUrl);
      reject(new Error('Image loading failed'));
    };

    img.src = blobUrl;
  });
};